import { useEffect, useRef } from 'react';

const DEFAULT_THRESHOLD = 0.5;

/**
 * Fires `onImpression` once, when the element first becomes >=threshold visible.
 * SSR-safe: no-op without IntersectionObserver.
 */
export const useImpression = <T extends HTMLElement = HTMLDivElement>(
    onImpression: () => void,
    { threshold = DEFAULT_THRESHOLD }: { threshold?: number } = {}
) => {
    const ref = useRef<T>(null);
    const firedRef = useRef(false);
    const callbackRef = useRef(onImpression);
    callbackRef.current = onImpression;

    useEffect(() => {
        const element = ref.current;
        if (!element || firedRef.current || typeof IntersectionObserver === 'undefined') {
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !firedRef.current) {
                    firedRef.current = true;
                    callbackRef.current();
                    observer.disconnect();
                }
            },
            { threshold }
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [threshold]);

    return ref;
};
